// The pallets of one variety line on the intake form (spec #4006 §4.1), where
// the business weighs at intake. One row per pallet off the truck: crates and
// gross. A new row copies the last row's crates. One pallet tare for the line
// applies to each pallet. The line's crate count is the rows' sum — the parent
// keeps its bins field equal to it, so totals, empties and bin loans read the
// same number as before.

import { useMemo } from 'react'
import { Plus, X } from 'lucide-react'
import {
  newPalletDraft, palletLineProblem, palletLineSums, palletNetKg, type PalletDraft,
} from '@/lib/deliveryPallets'
import { parseKg } from '@/lib/intakeWeight'

// One row of inputs per pallet, plus the line summary.
// fallow-ignore-next-line complexity
export default function IntakePalletsField({ idPrefix, rows: value, palletTare, crateTareKg, onChange, onPalletTare }: {
  idPrefix: string
  rows: PalletDraft[]
  palletTare: string
  /** One crate's tare for this line's crate type, or null when it has none. */
  crateTareKg: number | null
  /** Rows changed: the parent stores them and sets the line's bins to the sum. */
  onChange: (rows: PalletDraft[], crates: number) => void
  onPalletTare: (raw: string) => void
}) {
  // An untouched line shows one blank pallet; it becomes real on the first keystroke.
  const blank = useMemo(() => newPalletDraft(), [])
  const rows = value.length ? value : [blank]
  const tareKg = parseKg(palletTare)
  const sums = palletLineSums(rows, tareKg, crateTareKg)
  const problem = rows.some(r => r.crates.trim() || r.gross.trim()) ? palletLineProblem(rows, palletTare, crateTareKg) : null

  const emit = (next: PalletDraft[]) => onChange(next, palletLineSums(next, tareKg, crateTareKg).crates)
  const patch = (i: number, p: Partial<PalletDraft>) => emit(rows.map((r, j) => (j === i ? { ...r, ...p } : r)))

  return (
    <div className="space-y-2" data-testid={`${idPrefix}-pallets`}>
      <div className="flex items-end justify-between gap-3">
        <p className="label mb-0">Pallets <span className="text-danger">*</span></p>
        <label className="block w-40">
          <span className="text-caption text-fg-muted">Pallet tare each (kg)</span>
          <input className="field" type="text" inputMode="decimal" autoComplete="off"
            aria-label="Pallet tare each (kg)" placeholder="0"
            value={palletTare} onChange={e => onPalletTare(e.target.value)} />
        </label>
      </div>
      <div className="grid grid-cols-[2.5rem_1fr_1fr_5rem_2.75rem] gap-2 items-center text-caption text-fg-muted">
        <span>#</span><span>Crates</span><span>Gross kg</span><span className="text-right">Net ≈</span><span />
      </div>
      {rows.map((r, i) => {
        const c = parseInt(r.crates) || 0
        const net = palletNetKg(parseKg(r.gross), c, crateTareKg, tareKg ?? 0)
        return (
          <div key={r.id} className="grid grid-cols-[2.5rem_1fr_1fr_5rem_2.75rem] gap-2 items-center">
            <span className="font-semibold tabular-nums text-fg">{i + 1}</span>
            <input className="field text-lg font-bold" type="text" inputMode="numeric" autoComplete="off"
              aria-label={`Pallet ${i + 1} crates`} placeholder="Crates"
              value={r.crates} onChange={e => patch(i, { crates: e.target.value.replace(/[^0-9]/g, '') })} />
            <input className="field" type="text" inputMode="decimal" autoComplete="off"
              aria-label={`Pallet ${i + 1} gross weight (kg)`} placeholder="kg"
              value={r.gross} onChange={e => patch(i, { gross: e.target.value })} />
            <span className="text-right tabular-nums text-sm text-fg-secondary">{net != null ? `${net} kg` : '—'}</span>
            <button type="button" disabled={rows.length === 1}
              onClick={() => emit(rows.filter((_, j) => j !== i))}
              className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-fg-muted hover:text-danger disabled:opacity-30"
              aria-label={`Remove pallet ${i + 1}`}>
              <X size={16} />
            </button>
          </div>
        )
      })}
      <button type="button" onClick={() => emit([...rows, newPalletDraft(rows[rows.length - 1])])}
        className="w-full rounded-xl border-2 border-dashed border-line py-2.5 flex items-center justify-center gap-2
                   text-sm text-fg-muted hover:border-green-500 hover:text-success transition-colors">
        <Plus size={16} /> Add pallet
      </button>
      {problem ? (
        <p className="text-caption text-danger">{problem}</p>
      ) : (
        <p className="text-caption text-fg-secondary tabular-nums">
          {sums.pallets} pallet{sums.pallets === 1 ? '' : 's'} · {sums.crates} crates
          {sums.grossKg != null && ` · ${sums.grossKg} kg gross`}
          {sums.netKg != null
            ? ` · net ≈ ${sums.netKg} kg`
            : sums.weighed > 0 && sums.weighed < sums.pallets
              ? ' · weigh every pallet to see the net'
              : sums.weighed > 0 && crateTareKg == null
                ? ' · pick the crate type (with its tare) to work out the net'
                : ''}
        </p>
      )}
    </div>
  )
}
